import { RubyLoader } from '../components/RubyLoader';
/**
 * IN-APP ISSUES QUEUE — Ruby chrome (black bar flush to top/sidebar, ruby
 * underline, light content), with the issue detail as a slide-over drawer on
 * top of the list (route matches /app-issues/* — the id segment opens the
 * drawer, so deep links and the browser back button both work and the list
 * never unmounts or reloads behind it).
 *
 * Quick actions per row — no need to open the issue:
 *   - status popover (change status in place)
 *   - AI prompt -> straight to clipboard
 *   - delete (confirm modal)
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  Bug, AlertCircle, ChevronDown, Trash2, Check, Terminal, ShieldCheck,
  Smartphone, Monitor, Users, Megaphone, ImageOff, ExternalLink, Lock, PanelRightOpen, Cpu, type LucideIcon,
} from 'lucide-react';
import ShimmerImage from '../components/ShimmerImage';
import { RubyGemMark } from '../components/RubyGemMark';
/*
  A ROBOT, NOT A SPARKLE. `Sparkles` is the generic "AI" mark every product
  shipped the same year; the owner's words were "remove this common AI icon and
  put robo icon or something". `RobotIcon` is the console's own stroke SVG, so
  the queue row and the console's primary action carry the same glyph.
*/
import { RobotIcon } from './console/RobotIcon';
import { issuesApi, AdminListFilters } from '../services/issuesApi';
import { toastService } from '../services/toastService';
import { StaffPage, StaffSubtabs } from '../components/staff/StaffChrome';
import AppIssueDrawer, { ISSUE_STATUSES } from './SuperAdminAppIssueDetail';
import { isDevHost, devConsoleUrl } from '../config/devOnly';
import {
  consoleApi, ConsoleApprovalRow, rowKeyOf, liveLock, lockHolderName, lockFromError, currentUserId,
} from '../services/consoleApi';
import { CONSOLE_THEME as CT } from './console/theme';
import {
  deriveSource, priorityMeta, statusMeta, ISSUE_CHANNEL, MetaTone, SourceKind,
  autoTicketFacts, DEVICE_AUTO_CATEGORY, DEVICE_AUTO_LABEL,
} from './console/issueMeta';

/*
  RULING 39 -- THE CONSOLE SCREENS ARE LAZY.

  Their routes are registered only on a dev host (see the splat owner at the
  foot of this file), but a STATIC import puts them in the bundle regardless:
  ~900 lines of tester console, diff review, verification gate, pipeline,
  rollback and spend ledger shipped to admin.rubytill.com, where no route can
  ever reach them. `React.lazy` makes "absent on prod" true of the BYTES as
  well as the routes -- the chunk is only fetched when a dev-host route
  actually renders one.

  Named exports, so each import maps the name onto `default` for lazy().
*/
const TesterConsole = React.lazy(() =>
  import('./console/TesterConsole').then((m) => ({ default: m.TesterConsole })));
const DiffReview = React.lazy(() =>
  import('./console/DiffReview').then((m) => ({ default: m.DiffReview })));
const VerifyDeploy = React.lazy(() =>
  import('./console/VerifyDeploy').then((m) => ({ default: m.VerifyDeploy })));
const PipelineView = React.lazy(() =>
  import('./console/PipelineView').then((m) => ({ default: m.PipelineView })));
const RollbackView = React.lazy(() =>
  import('./console/RollbackView').then((m) => ({ default: m.RollbackView })));
const SpendLedger = React.lazy(() =>
  import('./console/SpendLedger').then((m) => ({ default: m.SpendLedger })));

/**
 * The wrapper every lazy console route gets. The fallback is deliberately
 * quiet: the console's own theme is inside the chunk, so anything more
 * elaborate here would flash the wrong surface's colours for one frame.
 */
const ConsoleChunk: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense
    fallback={
      <div
        style={{ minHeight: '100vh', background: CT.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        aria-busy="true"
      >
        <RubyGemMark size={72} drawing title="Loading the console" />
      </div>
    }
  >
    {children}
  </React.Suspense>
);

const PAGE_SIZE = 50;

/** Minimum hit area for anything on the approval board. */
const TAP = 44;

type Tab = 'all' | 'new' | 'in_progress' | 'awaiting_retest' | 'awaiting_owner_approval' | 'resolved';

const STATUS_LABEL: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  awaiting_retest: 'Awaiting Retest',
  awaiting_owner_approval: 'Awaiting Approval',
  resolved: 'Resolved',
  rejected: 'Rejected',
};

/*
  THE PILLS. One vocabulary with the console rail (./console/issueMeta), drawn
  here on light: a soft tint, an inset hairline and a status dot -- not the old
  saturated blobs. Only `critical` is solid ruby, because it is the one state
  that should shout. No purple / violet / indigo / fuchsia (rubytill-theme).
*/
const LIGHT_TONE: Record<MetaTone, { pill: string; dot: string }> = {
  ruby:     { pill: 'bg-[#DC143C] text-white ring-[#DC143C]', dot: 'bg-white' },
  rubySoft: { pill: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500' },
  amber:    { pill: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500' },
  green:    { pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  sky:      { pill: 'bg-sky-50 text-sky-700 ring-sky-200', dot: 'bg-sky-500' },
  slate:    { pill: 'bg-stone-50 text-stone-600 ring-stone-200', dot: 'bg-stone-400' },
};

const PILL = 'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[12px] font-medium ring-1 ring-inset whitespace-nowrap';

/** Every card action is this shape and size: 44px, rounded-xl, icon + label. */
const ACTION = 'px-3 shrink-0 rounded-xl text-[13px] font-semibold inline-flex items-center gap-2 transition-colors whitespace-nowrap';

const fmtClock = (ts: number): string =>
  new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const TonePill: React.FC<{ tone: MetaTone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`${PILL} ${LIGHT_TONE[tone].pill}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${LIGHT_TONE[tone].dot}`} />
    {children}
  </span>
);

const SOURCE_ICON: Record<SourceKind, LucideIcon> = {
  'pos-auto': Cpu,
  'pos-terminal': Smartphone,
  admin: ShieldCheck,
  portal: Users,
  advertising: Megaphone,
  web: Monitor,
};

const SourcePill: React.FC<{ iss: any }> = ({ iss }) => {
  const src = deriveSource(iss);
  const Icon = SOURCE_ICON[src.kind];
  return (
    <span className={`${PILL} bg-white text-stone-700 ring-stone-200`}>
      <Icon size={13} className="text-stone-500" />
      {src.label}
    </span>
  );
};

/*
  THE SCREENSHOT THUMBNAIL. The row carries only `screenshotKey`; the viewable
  URL is a presigned GET from the service. Fetched when the card scrolls into
  view (not 50 presigns on first paint) and cached for the page's life so a
  background refresh does not re-sign every image.
*/
const shotCache = new Map<string, Promise<string | null>>();

const IssueThumb: React.FC<{ iss: any }> = ({ iss }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const has = !!iss.screenshotKey;

  useEffect(() => {
    if (!has || !ref.current) return undefined;
    let cancelled = false;
    const load = () => {
      let p = shotCache.get(iss.id);
      if (!p) {
        p = issuesApi.getScreenshotUrl(iss.id).then((r) => r.url || null).catch(() => null);
        shotCache.set(iss.id, p);
      }
      p.then((u) => { if (cancelled) return; if (u) setUrl(u); else setFailed(true); });
    };
    if (typeof IntersectionObserver === 'undefined') { load(); return () => { cancelled = true; }; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
    }, { rootMargin: '200px' });
    io.observe(ref.current);
    return () => { cancelled = true; io.disconnect(); };
  }, [has, iss.id]);

  return (
    <div
      ref={ref}
      className="w-full h-full rounded-xl overflow-hidden bg-stone-100 ring-1 ring-inset ring-stone-200 flex items-center justify-center"
    >
      {has && !failed ? (
        <ShimmerImage
          src={url}
          alt={`Screenshot for ${iss.title || 'issue'}`}
          width="100%"
          height="100%"
          objectFit="cover"
          imgClassName="object-top"
          className="w-full h-full"
          fallbackIcon={<ImageOff size={18} className="text-stone-400" />}
        />
      ) : (
        <div className="flex flex-col items-center gap-1 text-stone-400">
          <ImageOff size={18} />
          <span className="text-[10px]">{has ? 'Unavailable' : 'No screenshot'}</span>
        </div>
      )}
    </div>
  );
};

const fmtAgo = (ts: number): string => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const SkeletonRows: React.FC = () => (
  <div className="relative">
  <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3 animate-pulse">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="bg-white border border-stone-200 rounded-2xl p-4 flex gap-4">
        <div className="flex-1">
          <div className="h-4 bg-stone-200 rounded w-1/2 mb-2" />
          <div className="h-3 bg-stone-100 rounded w-5/6 mb-4" />
          <div className="flex gap-1.5 mb-3">
            <div className="h-7 w-24 bg-stone-100 rounded-lg" /><div className="h-7 w-20 bg-stone-100 rounded-lg" />
          </div>
          <div className="h-3 bg-stone-100 rounded w-2/3 mb-4" />
          <div className="flex gap-2"><div className="h-11 w-28 bg-stone-200 rounded-xl" /><div className="h-11 w-28 bg-stone-100 rounded-xl" /></div>
        </div>
        <div className="hidden sm:block w-44 h-32 bg-stone-100 rounded-xl" />
      </div>
    ))}
  </div>
  <RubyLoader variant="overlay" />
  </div>
);


const IssuesQueueList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState<Tab>('all');
  const [priority, setPriority] = useState<'all' | 'critical' | 'blocking' | 'annoying' | 'minor'>('all');
  /** Device-health auto tickets vs people-reported (spec 2026-10-04). */
  const [category, setCategory] = useState<'all' | 'auto' | 'people'>('all');
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [issues, setIssues] = useState<any[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  /** `loading` is FIRST LOAD ONLY -- nothing on screen yet. See the fetch below. */
  const [loading, setLoading] = useState(true);
  /** A newer set is on its way and the rows on screen are still good. */
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  /*
    Selected row ids for the bulk actions. Ids, not rows: a row object goes
    stale on the next background refresh, an id does not.
  */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** The bulk-delete confirmation. `null` = closed; a count is never inferred. */
  const [bulkConfirm, setBulkConfirm] = useState<string[] | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  /** Bumped to refetch in the background (console tab changed something). */
  const [refreshTick, setRefreshTick] = useState(0);

  /* The check-out lock, from the queue side. */
  const me = useMemo(() => currentUserId(), []);
  const [claimingFor, setClaimingFor] = useState<string | null>(null);
  const [takeoverFor, setTakeoverFor] = useState<any | null>(null);

  /*
    The current rows, readable without making the fetch effect depend on them.
    The effect needs to know only whether ANYTHING is on screen (load vs
    background refresh); depending on `issues` itself would refetch every time
    a refresh finished, forever.
  */
  const issuesRef = useRef<any[]>([]);
  issuesRef.current = issues;

  // Quick-action state
  const [statusMenuFor, setStatusMenuFor] = useState<string | null>(null);
  const [savingStatusFor, setSavingStatusFor] = useState<string | null>(null);
  const [copyingPromptFor, setCopyingPromptFor] = useState<string | null>(null);
  const [copiedFor, setCopiedFor] = useState<string | null>(null);
  const statusMenuRef = useRef<HTMLDivElement | null>(null);

  // The drawer id comes straight from the URL: /app-issues/<id>. The route is
  // app-issues/* so this component stays mounted either way — closing the
  // drawer (or pressing browser back) just drops the segment.
  const selectedId = useMemo(() => {
    const m = location.pathname.match(/^\/app-issues\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }, [location.pathname]);

  /*
    The owner's view of the SAME queue. It is a filter, not a page: pressing
    the chip swaps this body for the approval board and nothing else moves.
    Off a dev host the chip still filters — the board itself needs the console
    endpoints, which only exist on dev, so there it falls back to the ordinary
    status-filtered table.
  */
  const approvalMode = tab === 'awaiting_owner_approval' && isDevHost();

  /*
    THE LIST REFRESHES UNDERNEATH, IT DOES NOT RELOAD.

    "page should not refresh, it should refresh in the background, then when we
    go to every tab we can see." Changing a tab used to set `loading`, which
    dimmed the table to 50% and turned off its pointer events -- so switching
    tabs blanked out the thing you were reading and took the row you were
    reaching for with it.

    Now `loading` means ONLY "there is nothing on screen yet" (the first load,
    where a skeleton is the honest thing to draw) and `refreshing` means "these
    rows are real but a newer set is on its way". Rows stay live and clickable
    throughout; the swap happens when the data arrives, not when the request
    starts.
  */
  const filtersFor = useCallback((cursor?: string): AdminListFilters => {
    const f: AdminListFilters = { limit: PAGE_SIZE };
    // No cast: AdminListFilters['status'] now carries awaiting_owner_approval
    // (widened in src/services/issuesApi.ts, 2026-09-26), so the tab's value is
    // simply a valid filter.
    if (tab !== 'all') f.status = tab;
    if (priority !== 'all') f.priority = priority as any;
    if (category === 'auto') f.category = DEVICE_AUTO_CATEGORY;
    if (category === 'people') f.notCategory = DEVICE_AUTO_CATEGORY;
    if (cursor) f.cursor = cursor;
    return f;
  }, [tab, priority, category]);

  /*
    WHERE THE PAGE WAS LOOKING, restored after a background swap.

    A refreshed page is usually a different LENGTH, and the browser clamps
    scrollTop to the new height as the rows commit -- so a quiet refresh that
    kept every row still threw the reader back to the top. Captured before the
    swap, reapplied in a layout effect after it, so the restore happens before
    the browser paints and there is no visible jump.
  */
  const restoreScrollTo = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (restoreScrollTo.current === null) return;
    const y = restoreScrollTo.current;
    restoreScrollTo.current = null;
    window.scrollTo({ top: y, behavior: 'auto' });
  }, [issues]);

  useEffect(() => {
    let cancelled = false;
    // The approval board fetches its own, richer rows. Asking issuesApi for the
    // same set as well would only race it into the empty state.
    if (approvalMode) { setLoading(false); setRefreshing(false); return () => { cancelled = true; }; }

    // Rows already on screen -> this is a background refresh, not a load.
    if (issuesRef.current.length > 0) {
      setRefreshing(true);
      restoreScrollTo.current = window.scrollY;
    } else {
      setLoading(true);
    }
    setError(null);

    issuesApi.list(filtersFor())
      .then((res) => {
        if (cancelled) return;
        setIssues(res.issues || []);
        setCounts(res.counts || {});
        setCategoryCounts(res.categoryCounts || {});
        setNextCursor(res.nextCursor);
        /*
          A SELECTION CANNOT SURVIVE A SET IT IS NO LONGER IN. Keeping ids that
          the new rows do not contain would leave "3 selected" on screen with
          one visible checkbox ticked -- and a bulk delete would then act on
          rows nobody could see. Narrowed to what actually arrived.
        */
        const present = new Set((res.issues || []).map((i: any) => i.id));
        setSelected((prev) => {
          const kept = [...prev].filter((id) => present.has(id));
          return kept.length === prev.size ? prev : new Set(kept);
        });
      })
      .catch((e) => !cancelled && setError(e?.message || 'Failed to load issues'))
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => { cancelled = true; };
  }, [approvalMode, filtersFor, refreshTick]);

  /*
    THE CONSOLE LIVES IN ANOTHER TAB. When it changes an issue it pings
    ISSUE_CHANNEL, and whenever this tab comes back into view (the console tab
    was closed, or the owner switched back) the list refreshes in the
    background -- same quiet swap as a tab change, rows stay live.
  */
  useEffect(() => {
    const bump = () => setRefreshTick((t) => t + 1);
    const onVisible = () => { if (document.visibilityState === 'visible') bump(); };
    document.addEventListener('visibilitychange', onVisible);
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel(ISSUE_CHANNEL);
      ch.onmessage = bump;
    } catch { /* no channel -- the visibility refresh covers it */ }
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      ch?.close();
    };
  }, []);

  // Close the status popover on any outside click.
  useEffect(() => {
    if (!statusMenuFor) return;
    const onDown = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusMenuFor(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [statusMenuFor]);

  const patchIssue = (updated: any) => {
    setIssues((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
  };

  const quickStatus = async (iss: any, status: string) => {
    setStatusMenuFor(null);
    if (status === iss.status) return;
    setSavingStatusFor(iss.id);
    try {
      const res = await issuesApi.update(iss.id, { status: status as any });
      patchIssue(res.issue || { ...iss, status });
      // Keep the header tab counts roughly in sync without a refetch.
      setCounts((c) => ({
        ...c,
        [iss.status]: Math.max(0, (c[iss.status] || 0) - 1),
        [status]: (c[status] || 0) + 1,
      }));
      toastService.success(`#${iss.id?.slice(-8)} → ${STATUS_LABEL[status] || status}`);
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to update status');
    } finally {
      setSavingStatusFor(null);
    }
  };

  /** Stamp a lock onto the DEV row only -- ids repeat across stages. */
  const patchLock = (id: string, lock: any) =>
    setIssues((prev) => prev.map((i) => (i.id === id && i.sourceStage !== 'prod' ? { ...i, lock } : i)));

  /*
    CLAIM & START: check the issue out, then open its console. The new tab is
    opened SYNCHRONOUSLY, inside the click, and pointed at the console once the
    claim lands -- a window.open after an await is a popup the browser blocks.
    A refused claim closes the blank tab and names who has it.
  */
  const claimAndOpen = async (iss: any, force = false) => {
    const url = `/app-issues/${encodeURIComponent(rowKeyOf(iss))}/console`;
    const w = window.open('about:blank', '_blank');
    setClaimingFor(iss.id);
    try {
      const { lock } = await consoleApi.lock(iss.id, force);
      patchLock(iss.id, lock);
      setTakeoverFor(null);
      if (w) { w.opener = null; w.location.href = url; } else window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      w?.close();
      const holder = lockFromError(e);
      if (holder) {
        patchLock(iss.id, holder);
        toastService.error(`${lockHolderName(holder)} has this issue checked out`);
      } else {
        toastService.error(e?.message || 'Could not claim this issue');
      }
    } finally {
      setClaimingFor(null);
    }
  };

  const quickCopyPrompt = async (iss: any) => {
    setCopyingPromptFor(iss.id);
    try {
      const res = await issuesApi.getAiPrompt(iss.id);
      await navigator.clipboard.writeText(res.prompt);
      setCopiedFor(iss.id);
      setTimeout(() => setCopiedFor((c) => (c === iss.id ? null : c)), 2000);
      toastService.success('AI prompt copied to clipboard');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to copy AI prompt');
    } finally {
      setCopyingPromptFor(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await issuesApi.delete(deleteCandidate.id);
      setIssues((prev) => prev.filter((i) => i.id !== deleteCandidate.id));
      setCounts((c) => ({
        ...c,
        [deleteCandidate.status]: Math.max(0, (c[deleteCandidate.status] || 0) - 1),
      }));
      setDeleteCandidate(null);
      toastService.success('Ticket deleted');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to delete issue');
    } finally {
      setDeleting(false);
    }
  };

  /* ------------------------------------------------- selection and bulk delete */

  const toggleRow = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /*
    SELECT-ALL MEANS "ALL THE ROWS ON SCREEN", never "all the rows there are".
    The list is paginated, so a header box that claimed the whole result set
    would arm a delete over rows nobody has seen -- and the count in the
    confirmation would be the only place that difference showed up.
  */
  const allOnPageSelected = issues.length > 0 && issues.every((i) => selected.has(i.id));
  const someSelected = selected.size > 0;
  const toggleAll = () => setSelected(
    allOnPageSelected ? new Set() : new Set(issues.map((i) => i.id)),
  );

  /*
    BULK DELETE. Irreversible, so it is confirmed by a modal that NAMES THE
    COUNT -- never a native `confirm()`, which is a platform rule and which
    silently does nothing inside the iOS webview (see CLAUDE.md, and
    `feedback_no_native_confirm_ios_webview`).

    Deletes run one request at a time rather than as a `Promise.all`: this is a
    destructive fan-out against a table with no batch endpoint, and a partial
    failure has to be REPORTABLE -- "4 of 7 deleted, #a1b2c3d4 failed" -- rather
    than an aggregate rejection that leaves the caller unable to say which rows
    are gone. Every row that did succeed is removed from the list either way,
    because it really is gone.
  */
  const confirmBulkDelete = async () => {
    if (!bulkConfirm) return;
    setBulkDeleting(true);
    setBulkError(null);
    const gone: string[] = [];
    const failed: string[] = [];
    for (const id of bulkConfirm) {
      try {
        await issuesApi.delete(id);
        gone.push(id);
      } catch {
        failed.push(id);
      }
    }
    if (gone.length) {
      const goneSet = new Set(gone);
      setIssues((prev) => prev.filter((i) => !goneSet.has(i.id)));
      setSelected((prev) => new Set([...prev].filter((id) => !goneSet.has(id))));
    }
    setBulkDeleting(false);
    if (failed.length === 0) {
      setBulkConfirm(null);
      toastService.success(`${gone.length} ticket${gone.length === 1 ? '' : 's'} deleted`);
      return;
    }
    // The modal STAYS OPEN on a partial failure. Closing it would report the
    // successes and hide the rest.
    setBulkError(
      `${gone.length} deleted, ${failed.length} could not be: ${
        failed.map((id) => `#${id.slice(-8)}`).join(', ')
      }`,
    );
    setBulkConfirm(failed);
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await issuesApi.list(filtersFor(nextCursor));
      setIssues((prev) => [...prev, ...(res.issues || [])]);
      setNextCursor(res.nextCursor);
    } catch (e: any) {
      setError(e?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const totalBlocking = useMemo(
    () => issues.filter((i) => i.priority === 'blocking' && (i.status === 'new' || i.status === 'in_progress')).length,
    [issues],
  );

  /*
    AWAITING APPROVAL IS A TAB, between Awaiting Retest and Resolved -- its real
    position in the flow: a tester has passed it, it is waiting on a human, it
    has not shipped. It used to be reachable only through a one-off pill below
    the strip, which meant a signed-off issue was findable only under "All".

    Labelled "Awaiting Approval", not "Awaiting Owner Approval": the strip is
    already tight and the owner is the only approver, so the word adds width
    without adding information.

    `Resolved` still carries no badge. The service's `counts` are computed from
    the rows THIS request returned, and the default union is deliberately not an
    archive (`IN_PLAY_STATUSES` excludes the terminal statuses) -- so a
    `counts.resolved` read off the All tab would be 0 and would render as a
    confident "no resolved issues". No badge is the honest rendering of a number
    this response does not carry.
  */
  const TABS = [
    { id: 'all', label: 'All' },
    { id: 'new', label: 'New', badge: counts.new || 0 },
    { id: 'in_progress', label: 'In Progress', badge: counts.in_progress || 0 },
    { id: 'awaiting_retest', label: 'Awaiting Retest', badge: counts.awaiting_retest || 0 },
    // "Awaiting Approval" moved to System → Controls (2026-09-27). Signed-off
    // issues still show under All.
    { id: 'resolved', label: 'Resolved' },
  ];

  /*
    "ISSUE CONSOLE", not "In-App Issues". The page is no longer a list of bug
    reports -- it is report -> fix -> verify -> approve -> ship. The console's
    own breadcrumb already reads `rubytill / issue-console / issue-<id>`, so this
    makes the navigation and the console agree instead of giving one thing two
    names. Renamed in the sidebar (components/Layout.tsx) and the staff panel
    permissions (pages/staff/panelPermissions.ts) with it.

    FULL WIDTH (`wide`). "this view is not matching with our other team at all,
    it should be wide." This page is a workspace, not a document: the table has
    six columns already fighting for room, and the 1400px cap left a
    stone-coloured margin either side that made it look narrower than every
    other screen in the admin.
  */
  return (
    <StaffPage
      section="System"
      icon={Bug}
      title="Issue Console"
      subtitle={`Report, fix, verify and ship${totalBlocking > 0 ? ` · ${totalBlocking} blocking` : ''}`}
      tabs={<StaffSubtabs tabs={TABS} active={tab} onChange={(id) => setTab(id as Tab)} />}
      wide
    >
      {/*
        THE ONE-OFF "Awaiting you" PILL IS GONE. It existed because that status
        had no tab; it has one now (see TABS), in its real position in the flow.
        A pill that duplicated a tab would be two controls for one filter, with
        no way to tell which one you had pressed.
      */}

      {/* Category — device-health auto tickets vs people (spec 2026-10-04). */}
      {(() => {
        const auto = categoryCounts[DEVICE_AUTO_CATEGORY] || 0;
        const people = Object.entries(categoryCounts).reduce((a, [k, v]) => (k === DEVICE_AUTO_CATEGORY ? a : a + v), 0);
        const pills: Array<{ id: 'all' | 'auto' | 'people'; label: string }> = [
          { id: 'all', label: 'All' },
          { id: 'auto', label: `${DEVICE_AUTO_LABEL} (${auto})` },
          { id: 'people', label: `Reported by people (${people})` },
        ];
        return (
          <div className="flex gap-2 mb-3 flex-wrap items-center">
            <span className="text-[12px] text-stone-500 mr-1">Category</span>
            {pills.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setCategory(p.id)}
                className={`min-h-[44px] px-4 rounded-full text-[13px] border ${
                  category === p.id
                    ? 'bg-red-600 border-red-600 text-white font-semibold'
                    : 'bg-white border-stone-300 text-stone-600 hover:bg-stone-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        );
      })()}

      {/* Priority filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'critical', 'blocking', 'annoying', 'minor'] as const).map((p) => (
          <button
            key={p}
            onClick={() => setPriority(p)}
            className={`px-3 py-1 rounded-lg text-[12px] border ${
              priority === p
                ? 'bg-red-50 border-red-200 text-red-800 font-medium'
                : 'bg-white border-stone-300 text-stone-600 hover:bg-stone-50'
            }`}
          >
            Priority: {p}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-3">
          {error}
        </div>
      )}

      {/*
        THE BULK BAR, only when something is selected. A permanently visible bar
        with a disabled Delete would put a destructive control on screen at all
        times for a list whose usual job is reading.
      */}
      {someSelected && !approvalMode && (
        <div className="mb-3 flex items-center gap-3 flex-wrap bg-white border border-stone-300 rounded-xl px-4 py-2.5 shadow-sm">
          <span className="text-sm font-semibold text-stone-800">
            {selected.size} selected
          </span>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs text-stone-500 hover:text-stone-800 underline"
          >
            Clear
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => { setBulkError(null); setBulkConfirm([...selected]); }}
            style={{ minHeight: TAP }}
            className="px-4 rounded-lg bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700 inline-flex items-center gap-2"
          >
            <Trash2 size={14} />
            Delete {selected.size} ticket{selected.size === 1 ? '' : 's'}
          </button>
        </div>
      )}

      {/*
        THE BACKGROUND-REFRESH HINT. A line, not a spinner over the table: the
        rows underneath are real and still clickable, and this only says that a
        newer set is on its way.
      */}
      {refreshing && (
        <div className="mb-2 text-[11px] text-stone-500 inline-flex items-center gap-1.5">
          <RubyGemMark size={11} drawing />
          Updating&hellip;
        </div>
      )}

      {approvalMode ? (
        <div className="bg-white border border-stone-200 rounded-xl p-8 text-center">
          <ShieldCheck size={28} className="mx-auto text-stone-300 mb-3" />
          <div className="text-stone-700 font-medium">Approvals moved to System → Controls</div>
          <Link to="/controls" className="inline-block mt-3 text-[13px] font-semibold" style={{ color: '#DC143C' }}>
            Open Controls →
          </Link>
        </div>
      ) : loading && issues.length === 0 ? (
        <SkeletonRows />
      ) : issues.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-xl p-12 text-center">
          <AlertCircle size={32} className="mx-auto text-stone-300 mb-3" />
          <div className="text-stone-600">No issues match these filters.</div>
        </div>
      ) : (
        /*
          NO `opacity-50 pointer-events-none` ANY MORE. That was the "page
          refresh" the owner was reacting to: changing a tab greyed the table out
          and switched off its clicks, so the rows you were reading became
          unreadable and unreachable at the same time. The rows on screen are
          real until the new ones arrive; `refreshing` says so in a line above.
        */
        <div>
          {/*
            SELECT-ALL, as a slim toolbar over the cards (there is no table
            header to hang it on any more). Still "all on screen", never "all
            there are" -- see `toggleAll`.
          */}
          <div className="flex items-center gap-3 px-1 mb-2 text-[12px] text-stone-500">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" style={{ minHeight: TAP }}>
              <input
                type="checkbox"
                checked={allOnPageSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected && !allOnPageSelected; }}
                onChange={toggleAll}
                aria-label={allOnPageSelected ? 'Clear selection' : 'Select all issues on this page'}
                className="w-4 h-4 accent-red-600 cursor-pointer"
              />
              Select all
            </label>
            <span className="text-stone-300">&middot;</span>
            <span>{issues.length} issue{issues.length === 1 ? '' : 's'}</span>
          </div>

          {/*
            ONE CARD PER ISSUE. Reading order is the owner's: the title, then
            what the reporter actually wrote, then where/who, and the actions
            underneath -- with the screenshot beside it, because the picture is
            usually the fastest way to know which issue this is.

            The card click still opens the drawer (unchanged behaviour); every
            control inside stops propagation so it never also opens it.
          */}
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
          {issues.map((iss) => {
            const tenantName = iss.context?.tenantName;
            const st = statusMeta(iss.status);
            const pr = priorityMeta(iss.priority);
            const isSel = selected.has(iss.id);
            return (
            <div
              key={rowKeyOf(iss)}
              data-testid="issue-card"
              onClick={() => navigate(`/app-issues/${iss.id}`)}
              className={`relative bg-white rounded-2xl border p-4 flex flex-col gap-3 cursor-pointer transition-all ${
                isSel
                  ? 'border-red-300 ring-2 ring-red-100 shadow-sm'
                  : 'border-stone-200 shadow-sm hover:shadow-md hover:border-stone-300'
              } ${statusMenuFor === iss.id ? 'z-20' : ''}`}
            >
              <div className="flex gap-4">
              <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggleRow(iss.id)}
                  aria-label={`Select ${iss.title || 'issue'}`}
                  className="w-4 h-4 accent-red-600 cursor-pointer"
                />
              </div>

              <div className="flex-1 min-w-0 flex flex-col">
                {/* Title, then the issue as the reporter wrote it */}
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold text-stone-900 leading-snug truncate">
                      {iss.title || 'Untitled issue'}
                    </div>
                    <div className={`mt-1 text-[13px] leading-relaxed line-clamp-2 ${iss.description ? 'text-stone-600' : 'text-stone-400 italic'}`}>
                      {iss.description || 'No description given.'}
                    </div>
                  </div>

                  {/* Status — click to change it in place */}
                  <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setStatusMenuFor(statusMenuFor === iss.id ? null : iss.id)}
                      title="Change status"
                      className={`${PILL} ${LIGHT_TONE[st.tone].pill} hover:brightness-95`}
                    >
                      {savingStatusFor === iss.id
                        ? <RubyGemMark size={12} drawing color="currentColor" />
                        : <span className={`w-1.5 h-1.5 rounded-full ${LIGHT_TONE[st.tone].dot}`} />}
                      {st.label}
                      <ChevronDown size={12} className="opacity-60" />
                    </button>
                    {statusMenuFor === iss.id && (
                      <div
                        ref={statusMenuRef}
                        className="absolute right-0 top-9 z-30 bg-white border border-stone-200 rounded-xl shadow-xl py-1 w-48"
                      >
                        {ISSUE_STATUSES.map((s) => (
                          <button
                            key={s.v}
                            onClick={() => quickStatus(iss, s.v)}
                            className={`w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 hover:bg-stone-50 ${
                              s.v === iss.status ? 'text-stone-900 font-semibold' : 'text-stone-600'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${LIGHT_TONE[statusMeta(s.v).tone].dot}`} />
                            <span className="flex-1">{s.l}</span>
                            {s.v === iss.status && <Check size={13} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Where it came from, how bad, where in the app */}
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <SourcePill iss={iss} />
                  <TonePill tone={pr.tone}>{pr.label}</TonePill>
                  {iss.sourceStage === 'prod' && <TonePill tone="rubySoft">PROD</TonePill>}
                  {iss.context?.route && (
                    <span
                      className="inline-flex items-center h-7 px-2 rounded-lg bg-stone-50 text-stone-500 font-mono text-[11px] ring-1 ring-inset ring-stone-200 max-w-[16rem] truncate"
                      title={iss.context.route}
                    >
                      {iss.context.route}
                    </span>
                  )}
                </div>

                {/* Auto tickets carry their own counts (spec §7 Issue Console changes). */}
                {(() => {
                  const f = autoTicketFacts(iss);
                  if (!f) return null;
                  return (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-stone-600">
                      <span className="font-semibold text-stone-900">{f.count} times</span>
                      {f.lastSeenAt && <span>&middot; last seen {fmtAgo(f.lastSeenAt)}</span>}
                      {f.devices.map((d) => (
                        <span key={d.deviceId} className="rounded-lg bg-stone-100 px-2 py-0.5">{`${d.label} · ${d.count}×`}</span>
                      ))}
                    </div>
                  );
                })()}

                {/* Who and which tenant */}
                <div className="mt-2.5 text-[12px] text-stone-500 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
                  <span className="text-stone-800 font-medium truncate">
                    {iss.reporterName || iss.reporterEmail || 'Unknown reporter'}
                  </span>
                  {iss.reporterEmail && iss.reporterName && (
                    <><span className="text-stone-300">&middot;</span><span className="truncate">{iss.reporterEmail}</span></>
                  )}
                  <span className="text-stone-300">&middot;</span>
                  <span className={tenantName ? 'text-stone-700' : 'text-stone-400'}>{tenantName || 'No tenant name'}</span>
                  <span className="text-stone-300">&middot;</span>
                  <span className="font-mono text-stone-400">#{iss.id?.slice(-8)}</span>
                  <span className="text-stone-300">&middot;</span>
                  <span>{fmtAgo(iss.createdAt)}</span>
                </div>

              </div>

              {/* The screenshot, beside everything else */}
              <div className="hidden sm:block w-44 h-32 shrink-0">
                <IssueThumb iss={iss} />
              </div>
              </div>

              {/*
                ACTIONS -- ONE ROW across the full card (under the screenshot
              too), so they never wrap onto a second line. Console first --
              the owner's main way in.

                CONSOLE OPENS IN A NEW TAB (`target="_blank"`). It is a full
                workspace, left by closing it; its rail links REPLACE rather
                than push, so that tab stays closable. `noopener` so it cannot
                script this tab back. It pings this tab over a
                BroadcastChannel when it changes an issue, and this list also
                refreshes when the tab regains focus -- so closing the console
                lands on an up-to-date queue.

                Console + Review stay dev-host only, as the routes are
                (src/config/devOnly.ts). The link carries `rowKeyOf(iss)`
                because ids repeat across stages (I2); the drawer keeps the
                bare id, which is what `issuesApi.get` reads.
              */}
              <div
                className="ml-8 pt-3 border-t border-stone-100 flex items-center gap-2 flex-nowrap overflow-x-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/*
                  THE CHECK-OUT LOCK on the card (support-service lib/issueLock.ts).
                    - someone else has it -> Console is DISABLED and says who; the
                      only way in is Take over (confirmed), so nobody wanders into
                      an issue another person is mid-way through.
                    - free -> Claim checks it out AND opens the console, in one
                      press; Console alone opens it without claiming.
                    - yours -> Continue.
                  Prod rows are never locked (they are not worked from the dev console).
                */}
                {isDevHost() && (() => {
                  const lockable = iss.sourceStage !== 'prod';
                  const held = lockable ? liveLock(iss.lock) : null;
                  const mine = !!held && held.userId === me;
                  const other = held && !mine ? held : null;
                  const busy = claimingFor === iss.id;
                  if (other) {
                    return (
                      <>
                        <span
                          aria-disabled="true"
                          title={`${lockHolderName(other)} is working on this issue`}
                          style={{ minHeight: TAP }}
                          className={`${ACTION} bg-stone-100 text-stone-400 border border-stone-200 cursor-not-allowed`}
                        >
                          <Terminal size={15} />
                          Console
                        </span>
                        <span className={`${PILL} h-9 bg-amber-50 text-amber-800 ring-amber-200 min-w-0 max-w-[15rem]`} title={`${lockHolderName(other)} is working on it`}>
                          <Lock size={13} />
                          <span className="truncate">{lockHolderName(other)} is working on it &middot; since {fmtClock(other.acquiredAt)}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setTakeoverFor(iss)}
                          style={{ minHeight: TAP }}
                          className={`${ACTION} bg-white border border-stone-200 text-stone-700 hover:border-rose-200 hover:text-rose-700 hover:bg-rose-50`}
                        >
                          Take over
                        </button>
                      </>
                    );
                  }
                  return (
                    <>
                      {lockable && !mine && (
                        <button
                          type="button"
                          onClick={() => claimAndOpen(iss)}
                          disabled={busy}
                          title="Check this issue out so nobody else works on it, and open the console"
                          style={{ minHeight: TAP }}
                          className={`${ACTION} bg-[#DC143C] text-white hover:bg-[#9F1239] shadow-sm disabled:opacity-60`}
                        >
                          {busy ? <RubyGemMark size={15} drawing color="currentColor" /> : <Lock size={15} />}
                          Claim
                        </button>
                      )}
                      <a
                        href={`/app-issues/${encodeURIComponent(rowKeyOf(iss))}/console`}
                        target="_blank"
                        rel="noopener"
                        title={mine ? 'You have this checked out — continue in the console' : 'Open the fix console in a new tab'}
                        style={{ minHeight: TAP }}
                        className={`${ACTION} bg-stone-950 text-white hover:bg-black no-underline shadow-sm`}
                      >
                        <Terminal size={15} className="text-rose-400" />
                        {mine ? 'Continue' : 'Console'}
                        <ExternalLink size={13} className="text-stone-400" />
                      </a>
                      {mine && held && (
                        <span className={`${PILL} h-9 bg-emerald-50 text-emerald-700 ring-emerald-200 min-w-0 max-w-[15rem]`}>
                          <Lock size={13} />
                          <span className="truncate">Checked out by you &middot; since {fmtClock(held.acquiredAt)}</span>
                        </span>
                      )}
                    </>
                  );
                })()}
                {/* Off dev, Console hands over to the dev admin in a new tab —
                    the console only executes on dev (src/config/devOnly.ts). */}
                {!isDevHost() && (
                  <a
                    href={devConsoleUrl(iss)}
                    target="_blank"
                    rel="noopener"
                    title="The console runs only in DEV — opens this issue on the dev admin"
                    style={{ minHeight: TAP }}
                    className={`${ACTION} bg-stone-950 text-white hover:bg-black no-underline shadow-sm`}
                  >
                    <Terminal size={15} className="text-rose-400" />
                    Console
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider bg-rose-500/20 text-rose-300">DEV</span>
                    <ExternalLink size={13} className="text-stone-400" />
                  </a>
                )}
                {isDevHost() && iss.status === 'awaiting_owner_approval' && (
                  <Link
                    to={`/app-issues/${encodeURIComponent(rowKeyOf(iss))}/review`}
                    title="Review this fix and decide whether to ship it"
                    style={{ minHeight: TAP }}
                    className={`${ACTION} bg-[#9F1239] text-white hover:bg-[#7f0f2d] no-underline shadow-sm`}
                  >
                    <ShieldCheck size={15} />
                    Review
                  </Link>
                )}
                <button
                  onClick={() => quickCopyPrompt(iss)}
                  disabled={copyingPromptFor === iss.id}
                  title="Copy AI prompt to clipboard"
                  aria-label="Copy AI prompt"
                  style={{ minHeight: TAP }}
                  className={`${ACTION} border ${
                    copiedFor === iss.id
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-white border-stone-200 text-stone-700 hover:border-rose-200 hover:text-rose-700 hover:bg-rose-50'
                  }`}
                >
                  {copyingPromptFor === iss.id
                    ? <RubyGemMark size={15} drawing color="currentColor" />
                    : copiedFor === iss.id
                      ? <Check size={15} />
                      : <RobotIcon size={15} />}
                  {copiedFor === iss.id ? 'Copied' : 'AI prompt'}
                </button>
                {/*
                  VIEW -- the side drawer (full report, screenshot, console output,
                  comments). Clicking the card body always opened it, but nothing SAID
                  so; this is the visible way in.
                */}
                <button
                  type="button"
                  onClick={() => navigate(`/app-issues/${iss.id}`)}
                  title="Open the full issue in the side panel"
                  style={{ minHeight: TAP }}
                  className={`${ACTION} border bg-white border-stone-200 text-stone-700 hover:border-stone-300 hover:bg-stone-50`}
                >
                  <PanelRightOpen size={15} />
                  View
                </button>
                <div className="flex-1" />
                {/*
                  DELETE, same shape and size as every other action, in red.
                  Disabled while someone else has the issue checked out --
                  deleting it from under them is the worst version of the conflict.
                */}
                {(() => {
                  const held = iss.sourceStage !== 'prod' ? liveLock(iss.lock) : null;
                  const blocked = !!held && held.userId !== me;
                  return (
                    <button
                      onClick={() => setDeleteCandidate(iss)}
                      disabled={blocked}
                      title={blocked ? `${lockHolderName(held!)} is working on this issue` : 'Delete this ticket'}
                      aria-label="Delete ticket"
                      style={{ minHeight: TAP }}
                      className={`${ACTION} border bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-50`}
                    >
                      <Trash2 size={15} />
                      Delete
                    </button>
                  );
                })()}
              </div>
            </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Load-more — only when the backend signals there's another page.
          The "All" tab returns no cursor (it unions 3 status queries), so
          this only shows for specific-status filters once they exceed
          PAGE_SIZE items. */}
      {!loading && nextCursor && (
        <div className="flex justify-center mt-4">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-4 py-2 rounded-lg bg-white border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {loadingMore ? <RubyGemMark size={14} drawing color="currentColor" /> : <ChevronDown size={14} />}
            {loadingMore ? 'Loading…' : `Load ${PAGE_SIZE} more`}
          </button>
        </div>
      )}
      {!loading && !nextCursor && issues.length >= PAGE_SIZE && (
        <div className="text-center text-xs text-stone-400 mt-4">End of results · {issues.length} loaded</div>
      )}

      {/* Detail drawer — mounted over the list; the list stays live behind it. */}
      {selectedId && (
        <AppIssueDrawer
          id={selectedId}
          onClose={() => navigate('/app-issues')}
          onIssueUpdated={patchIssue}
          onIssueDeleted={(deletedId) => setIssues((prev) => prev.filter((i) => i.id !== deletedId))}
        />
      )}

      {/*
        BULK DELETE CONFIRMATION. A custom modal, NEVER `window.confirm` -- that
        is a platform rule (CLAUDE.md: no native alert/confirm) and it silently
        does nothing inside the iOS webview, which would turn a guarded
        irreversible action into an unguarded one on exactly the devices that
        matter.

        IT NAMES THE COUNT in the heading and on the button, and lists the
        tickets. "Delete selected?" over a selection made three scrolls ago is
        how somebody deletes eleven rows meaning to delete one.
      */}
      {/* TAKE OVER, confirmed -- it moves someone else's work to you. */}
      {takeoverFor && (() => {
        const held = liveLock(takeoverFor.lock);
        return (
          <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-5 border-b border-stone-200 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <Lock size={18} className="text-amber-700" />
                </div>
                <div>
                  <div className="text-base font-semibold text-stone-900">
                    Take this issue over{held ? ` from ${lockHolderName(held)}` : ''}?
                  </div>
                  <div className="text-xs text-stone-500">Their console switches to read-only straight away.</div>
                </div>
              </div>
              <div className="p-5 text-sm text-stone-700">
                <span className="font-mono text-stone-500">#{takeoverFor.id?.slice(-8)}</span> — {takeoverFor.title}
              </div>
              <div className="p-4 bg-stone-50 border-t border-stone-200 flex justify-end gap-2 rounded-b-2xl">
                <button
                  onClick={() => setTakeoverFor(null)}
                  style={{ minHeight: TAP }}
                  className="px-4 text-sm rounded-xl border border-stone-300 text-stone-700 hover:bg-stone-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => claimAndOpen(takeoverFor, true)}
                  disabled={claimingFor === takeoverFor.id}
                  style={{ minHeight: TAP }}
                  className="px-4 text-sm rounded-xl bg-[#DC143C] text-white font-semibold hover:bg-[#9F1239] disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {claimingFor === takeoverFor.id && <RubyGemMark size={14} drawing color="currentColor" />}
                  Take over &amp; open
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {bulkConfirm && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-stone-200 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <div className="text-base font-semibold text-stone-900">
                  Delete {bulkConfirm.length} ticket{bulkConfirm.length === 1 ? '' : 's'}?
                </div>
                <div className="text-xs text-stone-500">This can&rsquo;t be undone.</div>
              </div>
            </div>
            <div className="p-5 text-sm text-stone-700">
              <div className="max-h-40 overflow-y-auto mb-3 border border-stone-200 rounded-lg divide-y divide-stone-100">
                {bulkConfirm.map((id) => {
                  const row = issues.find((i) => i.id === id);
                  return (
                    <div key={id} className="px-3 py-1.5 text-xs flex gap-2">
                      <span className="font-mono text-stone-400 shrink-0">#{id.slice(-8)}</span>
                      <span className="truncate text-stone-700">{row?.title || 'Untitled'}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-stone-500 text-xs">
                Each issue, all its comments, and its screenshot will be permanently removed.
              </p>
              {bulkError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-700">
                  {bulkError}
                </div>
              )}
            </div>
            <div className="p-4 bg-stone-50 border-t border-stone-200 flex justify-end gap-2 rounded-b-2xl">
              <button
                onClick={() => { setBulkConfirm(null); setBulkError(null); }}
                disabled={bulkDeleting}
                style={{ minHeight: TAP }}
                className="px-3 text-sm rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                style={{ minHeight: TAP }}
                className="px-4 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {bulkDeleting && <RubyGemMark size={14} drawing color="currentColor" />}
                Delete {bulkConfirm.length} forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline delete confirmation — same pattern as the drawer so the row
          trash icon behaves identically. */}
      {deleteCandidate && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-stone-200 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <div className="text-base font-semibold text-stone-900">Delete this ticket?</div>
                <div className="text-xs text-stone-500">This can't be undone.</div>
              </div>
            </div>
            <div className="p-5 text-sm text-stone-700">
              <p className="mb-2">
                <span className="font-mono text-stone-500">#{deleteCandidate.id?.slice(-8)}</span>{' '}
                — {deleteCandidate.title}
              </p>
              <p className="text-stone-500">The issue, all its comments, and the screenshot will be permanently removed.</p>
            </div>
            <div className="p-4 bg-stone-50 border-t border-stone-200 flex justify-end gap-2 rounded-b-2xl">
              <button
                onClick={() => setDeleteCandidate(null)}
                disabled={deleting}
                className="px-3 py-1.5 text-sm rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-1.5 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {deleting && <RubyGemMark size={14} drawing color="currentColor" />}
                Delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </StaffPage>
  );
};

/**
 * THE SPLAT OWNER. App.tsx registers `app-issues/*` and nothing below it, so
 * every sub-route of /app-issues is declared here.
 *
 * The console routes are registered ONLY on a dev host — not hidden, not
 * permission-gated: on admin.rubytill.com the <Route> element is never
 * created, so /app-issues/<id>/console simply falls through to the list. See
 * src/config/devOnly.ts.
 *
 * The catch-all keeps today's behaviour exactly: /app-issues and
 * /app-issues/:id both render the list (the id segment opens the drawer, which
 * reads it straight off the pathname), so adding a route here is additive.
 */
const SuperAdminAppIssuesQueue: React.FC = () => (
  <Routes>
    {isDevHost() && <Route path=":id/console" element={<ConsoleChunk><TesterConsole /></ConsoleChunk>} />}
    {isDevHost() && <Route path=":id/review" element={<ConsoleChunk><DiffReview /></ConsoleChunk>} />}
    {isDevHost() && <Route path=":id/verify" element={<ConsoleChunk><VerifyDeploy /></ConsoleChunk>} />}
    {isDevHost() && <Route path=":id/pipeline" element={<ConsoleChunk><PipelineView /></ConsoleChunk>} />}
    {isDevHost() && <Route path=":id/rollback" element={<ConsoleChunk><RollbackView /></ConsoleChunk>} />}
    {/*
      A STATIC SEGMENT, so it must not be read as an issue id. React Router
      ranks a literal above a dynamic segment regardless of order, so
      /app-issues/spend is the ledger and never issue "spend".
    */}
    {isDevHost() && <Route path="spend" element={<ConsoleChunk><SpendLedger /></ConsoleChunk>} />}
    <Route path="*" element={<IssuesQueueList />} />
  </Routes>
);

export default SuperAdminAppIssuesQueue;
